import type {
  CommitOption,
  RepositoryChangesetOrderByInput
} from '../__generated__/apollo-hooks';

export const RESULTS_PAGE_SIZE = 50;

// Sizes the split-view tree's loaded window. Shared with the heartbeat refetch
// floor so a poll can't shrink the tree below what it has loaded.
export const RESULTS_TREE_WINDOW_MULTIPLIER = 4;

// Repositories fetched per page when the tree loads one SCM organization.
export const ORGANIZATION_PAGE_SIZE = 100;

// The tree's window and its per-organization pages must share one order
// (moderneinc/customer-requests#3237).
export const RESULTS_TREE_ORDER_BY: RepositoryChangesetOrderByInput[] = [
  { field: 'RESULTS', direction: 'DESC' },
  { field: 'PATH', direction: 'ASC' }
];

// How often an in-progress run refreshes its organization list; the list is
// large and barely changes while a run is going.
export const ORGANIZATION_LIST_POLL_INTERVAL = 30_000;

/**
 * Label for the `showErrorResultsOnly` diff filter. Shared so the settings
 * panel, the repository error banner and the empty state cannot drift into
 * three names for one setting.
 */
export const ONLY_SHOW_ERRORS_LABEL = 'Only show errors';

export const DIFF_RESULT_PAGE_SIZE = 20;
export const DIFF_MAX_LINES_RENDERED = 200;
export const DIFF_MAX_CONTENT_SIZE_KB = 100 * 1024;

// Default diff code font size (px) when the user hasn't picked one. Drives both
// the settings picker and the rendered fallback so they can't drift.
export const DEFAULT_CODE_FONT_SIZE = 13;

// Registered name of the "Source files that errored on a recipe" table, which a
// RECIPE_ERROR repo's popover offers for download. Both the `_eq` filter in
// recipeRunNamedDataTable and the downloadDataTable mutation match it exactly,
// so this must be the full name — unlike chrono-devcenter, which does `endsWith`
// matching over an already-fetched list.
export const SOURCES_FILE_ERRORS_TABLE =
  'org.openrewrite.table.SourcesFileErrors';

export const SEARCH_RESULTS_TABLE = 'org.openrewrite.table.SearchResults';

export type CommitStrategyDetails = {
  value: CommitOption;
  label: string;
  help?: string;
};

export const COMMIT_STRATEGY_DETAILS: CommitStrategyDetails[] = [
  {
    value: 'DIRECT',
    label: 'Commit directly',
    help: `Commit **directly** to the repository on the default branch.

  _Requires write permissions to the repository._`
  },
  {
    value: 'BRANCH',
    label: 'Create a branch',
    help: `Create a **branch** on the repository with the results.

  _Requires write permissions to the repository._`
  },
  {
    value: 'FORK',
    label: 'Create a fork',
    help: `Commit the results to a **branch** on a **fork** of the repository under your own account.

**Note**: Override default fork location by setting the __Organization/project name__ field.`
  },
  {
    value: 'PULL_REQUEST',
    label: 'Create a pull request',
    help: `Create a **branch** on the repository with the results and open a **pull request** for the default branch.

  **Notes**:
  - _Requires write permissions to the repository_`
  },
  {
    value: 'FORK_AND_PULL_REQUEST',
    label: 'Create a pull request from a fork',
    help: `Create a **pull request** from a **branch** of a **fork** of the repository under your own account.

**Note**:
- Override default fork location by setting the **Organization/project name** field.`
  },
  {
    value: 'NONE',
    label: 'No commit',
    help: 'Do not commit the results to the repository.'
  }
];
